import React, { useEffect, useMemo, useState } from 'react'
import { startOfDay, subDays, endOfDay } from 'date-fns'
import { useParams } from 'react-router-dom'
import { fromZonedTime } from 'date-fns-tz'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { GiftCardsTable } from '@/pages/gift-cards/gift-cards-table'

import {
  fetchRewardsAnalytics,
  selectRewardsAnalytics,
} from '../../../../../reducers/rewardsReducer'
import {
  selectBuildingsForFilter,
  selectActiveBuilding,
} from '../../../../../reducers/buildingSelectorReducer'
import { getGiftCardsAnalyticsMapping } from '../../helpers'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'
import { selectUserRole } from '../../../../../reducers/userDataReducer'
import AnalyticsItem from '../../components/AnalyticsItem/AnalyticsItem'
import Upselling from '../../../../Upselling/Upselling'
import Icon from '../../../../Icons'

const DEFAULT_DAYS_RANGE = 30
const INITIAL_DATE_FILTER = [
  subDays(startOfDay(Date.now()), DEFAULT_DAYS_RANGE),
  startOfDay(Date.now()),
]

const RewardsTable = ({ fetchRewardsAnalytics, buildings, analytics, buildingData, userRole }) => {
  const { t } = useTranslation(['rewards-table', 'rewards-dashboard'])
  const [isLoadingAnalytics, setLoadingAnalytics] = useState(true)
  const [showAnalytics, setShowAnalytics] = useState(true)
  const [dateFilter, setDateFilter] = useState(INITIAL_DATE_FILTER)

  const { displaySnackbar } = useSnackbar()
  const { buildingId } = useParams()

  const { rewardsAvailable } = buildingData || {}

  const selectedBuilding = useMemo(
    () => buildings.find((building) => building.id === Number(buildingId)),
    [buildings, buildingId],
  )

  const fetchAnalytics = async () => {
    const [startDate, endDate] = dateFilter
    if (startDate && endDate) {
      setLoadingAnalytics(true)
      const startTimestamp = fromZonedTime(startDate, 'UTC').getTime()
      const endTimestamp = fromZonedTime(endOfDay(endDate), 'UTC').getTime()

      await fetchRewardsAnalytics({
        buildingId: buildingId,
        startDate: startTimestamp,
        endDate: endTimestamp,
      }).catch(({ message }) => displaySnackbar(message, true))
      setLoadingAnalytics(false)
    }
  }

  useEffect(() => {
    if (buildingId) {
      fetchAnalytics()
    }
  }, [dateFilter, buildingId])

  useEffect(() => {
    setDateFilter(INITIAL_DATE_FILTER)
  }, [buildingId])

  if (!rewardsAvailable && userRole !== 'Administrator') {
    return (
      <div className="rewards-container">
        <Upselling
          params={{ type: 'giftCards' }}
          containerStyle={{ height: 'calc(100vh - 320px)' }}
        />
      </div>
    )
  }

  return (
    <React.Fragment>
      {analytics && selectedBuilding && (
        <div className="rewards-container">
          <div
            className={`rewards-dashboard rewards-dashboard--giftcards-tab ${
              showAnalytics ? '' : 'hidden'
            }`}
          >
            <span className="rewards-dashboard__title">
              {t('rewards-dashboard:giftCardsTitle')}
              <button
                className="button button-black-outline"
                onClick={() => setShowAnalytics(!showAnalytics)}
              >
                <Icon icon={`${showAnalytics ? 'showEye' : 'hideEye'}`} />
                {showAnalytics
                  ? t('rewards-dashboard:hideButton')
                  : t('rewards-dashboard:showButton')}
              </button>
            </span>
            <div className={`rewards-dashboard__wrapper ${isLoadingAnalytics ? 'is-loading' : ''}`}>
              <AnalyticsItem
                setDateFilter={setDateFilter}
                dateFilter={dateFilter}
                analytics={analytics}
                mapping={getGiftCardsAnalyticsMapping(t)}
              />
            </div>
          </div>
        </div>
      )}
      <div className="rewards-container">
        <GiftCardsTable building={selectedBuilding ?? null} />
      </div>
    </React.Fragment>
  )
}

RewardsTable.propTypes = {
  fetchRewardsAnalytics: PropTypes.func,
  buildings: PropTypes.array,
  analytics: PropTypes.object,
  buildingData: PropTypes.object,
  userRole: PropTypes.string,
}

export default connect(
  (state) => ({
    userRole: selectUserRole(state),
    buildings: selectBuildingsForFilter(state),
    analytics: selectRewardsAnalytics(state),
    buildingData: selectActiveBuilding(state),
  }),
  {
    fetchRewardsAnalytics,
  },
)(RewardsTable)
